import { useState, useEffect, useRef } from 'react'
import { searchActors } from '../api/tmdb'
import { ACTOR_POOL } from '../utils/dailyPuzzleGen'
import ActorPickerView from './ActorPickerView'
import { IconArrowRight } from './icons'

function shuffleArray(arr) {
  const a = [...arr]
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]]
  }
  return a
}

/**
 * Stateful wrapper around the shared ActorPickerView for party + H2H flows.
 * Owns search, random-pick queue, and confirm state; delegates ALL chrome
 * to ActorPickerView so the look matches the Single-Player picker exactly.
 *
 * Props
 *   slotRole       — 'goal' (pick goal first, no start yet) | 'start' (pick
 *                    start while goal is already locked in via `goalActor`).
 *                    Ignored entirely when blindPick=true.
 *   goalActor      — when slotRole='start', the locked goal actor passed in;
 *                    rendered as the right slot.
 *   blindPick      — single-slot centered picker for party blind/anonymous
 *                    picks. Shows a shimmer "Pick an Actor" headline.
 *   confirmLabel   — override the confirm button text (e.g. "Lock In Actor"
 *                    for blind picks). Default: "Set the Goal" / "Start Game".
 *   onConfirm(a)   — called with the picked actor on submit.
 *   onCancel       — optional; renders a "Stay on Set" ghost button when set.
 */
export default function ActorPicker({
  slotRole = 'goal',
  goalActor = null,
  onConfirm,
  onCancel,
  confirmLabel: confirmLabelOverride,
  headline: headlineOverride,
  blindPick = false,
}) {
  const [pickedActor, setPickedActor] = useState(null)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [loadingRandom, setLoadingRandom] = useState(false)
  const [genderFilter, setGenderFilter] = useState('any')
  const queueRef = useRef({ filter: 'any', queue: [] })
  const inputRef = useRef(null)

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return }
    const t = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await searchActors(query.trim())
        setResults(data.filter((p) => p.profile_path).slice(0, 12))
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 350)
    return () => clearTimeout(t)
  }, [query])

  const handlePickActor = (actor) => {
    setPickedActor(actor)
    setQuery('')
    setResults([])
  }

  const handleClearPicked = () => {
    setPickedActor(null)
    setTimeout(() => inputRef.current?.focus(), 0)
  }

  const handleRandom = async () => {
    setLoadingRandom(true)
    try {
      const current = queueRef.current
      let queue = current.queue
      if (current.filter !== genderFilter || queue.length === 0) {
        const pool = genderFilter === 'any'
          ? ACTOR_POOL
          : ACTOR_POOL.filter((a) => a.gender === genderFilter)
        queue = shuffleArray(pool)
      }
      const entry = queue[queue.length - 1]
      queueRef.current = { filter: genderFilter, queue: queue.slice(0, -1) }
      if (!entry) return
      const data = await searchActors(entry.name)
      const actor = data.find((r) => r.name.toLowerCase() === entry.name.toLowerCase()) || data[0]
      if (actor) handlePickActor(actor)
    } catch {
      // ignore
    } finally {
      setLoadingRandom(false)
    }
  }

  const handleConfirm = () => {
    if (pickedActor) onConfirm?.(pickedActor)
  }

  const isPickingGoal  = slotRole === 'goal'
  const isPickingStart = slotRole === 'start'

  // ── Build the slots array for ActorPickerView ────────────────────────────
  // Blind: a single centered slot. Non-blind: START (left) → arrow → GOAL.
  // The active slot is whichever side the user is currently picking.
  let slots
  if (blindPick) {
    slots = [{
      role: 'PICK',
      label: 'Choose your actor',
      actor: pickedActor,
      state: 'active',
      onSelect: undefined,
      onClear: pickedActor ? handleClearPicked : null,
    }]
  } else if (isPickingGoal) {
    // Picking the GOAL first — left START slot is empty, GOAL is the active pick.
    slots = [
      { role: 'START', label: 'Choose start actor', actor: null, state: 'inactive', onSelect: undefined, onClear: null },
      { role: 'GOAL',  label: 'Choose goal actor',  actor: pickedActor, state: 'active',
        onSelect: undefined, onClear: pickedActor ? handleClearPicked : null },
    ]
  } else {
    // Picking the START — GOAL is already locked in (passed via `goalActor`).
    slots = [
      { role: 'START', label: 'Choose start actor', actor: pickedActor, state: 'active',
        onSelect: undefined, onClear: pickedActor ? handleClearPicked : null },
      { role: 'GOAL',  label: 'Choose goal actor',  actor: goalActor, state: 'inactive', onSelect: undefined, onClear: null },
    ]
  }

  // Submit label — blind picks override (e.g. "Lock In Actor"); START/GOAL
  // picks render the label + an IconArrowRight to replace the literal "→".
  const submitLabel = confirmLabelOverride
    ? confirmLabelOverride
    : (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span>{isPickingGoal ? 'Set the Goal' : 'Start Game'}</span>
        <IconArrowRight size={18} aria-hidden="true" />
      </span>
    )

  // The selectingLabel only makes sense for the START→GOAL flow.
  const selectingLabel = blindPick
    ? null
    : (isPickingGoal ? 'Goal Actor' : 'Start Actor')

  return (
    <ActorPickerView
      header={!blindPick}
      headline={blindPick ? (headlineOverride ?? 'Pick an Actor') : null}
      slots={slots}
      selectingLabel={selectingLabel}
      genderFilter={genderFilter}
      onGenderChange={setGenderFilter}
      query={query}
      onQueryChange={setQuery}
      inputRef={inputRef}
      searching={searching}
      results={results}
      onResultPick={handlePickActor}
      noResults={!searching && query.trim().length >= 2 && results.length === 0}
      rolling={loadingRandom}
      onRoll={handleRandom}
      submitLabel={submitLabel}
      canSubmit={!!pickedActor}
      onSubmit={handleConfirm}
      cancelSlot={onCancel ? (
        <button type="button" className="btn-ghost actor-picker__cancel" onClick={onCancel}>
          Stay on Set
        </button>
      ) : null}
    />
  )
}
